/**
 * Autoplay emissions: hitsounds and hit-effect bursts (W1-06; R§5.1, R§5.4, S§4.1, S§5, A§7.2).
 *
 * For every NON-FAKE note on EVERY line (UI-mover and negative-alpha lines included: their notes are still judged):
 *   Tap / Drag / Flick  'hit'       at time      sound = note hitsound            burst
 *   Hold                'holdStart' at time      sound = note hitsound (click)    no burst
 *                       'holdTick'  at time + 1 frame + k * 0.15/rate while < endTime   burst, no sound
 *                       'holdEnd'   at endTime                                    burst, no sound
 * Ticks model Phira's "first frame with t > tj + k * 0.15/rate" on a 60 Hz frame grid: the first tick lands one
 * frame after the head. Sounds: Tap and Hold -> click, Drag -> drag, Flick -> flick; the aliases "tap.mp3",
 * "flick.mp3", "drag.mp3" map to the skin sounds; any other string is a package file (custom hitsound asset).
 * The rate-independent part is rebuilt lazily when any runtime changed; tick lists are cached per rate.
 */

import type { AssetId, LineId, NoteKind } from '../../model/types';
import type { RGB } from '../../util/types';
import type { Emission, EmissionKind, EngineCompileContext, PreviewFilters } from '../types';
import { Search } from '../../math/search';
import { FRAME_DT, HOLD_TICK_INTERVAL } from './constants';
import { NOTE_DRAG, NOTE_FLICK, NOTE_HOLD, type LineNotesRt } from './runtime';
import { cacheOf, runtimesInOrder, syncAll } from './store';
import { lineFilteredOut } from './visibility';

/** Emission with the note data the hit-effect pass needs (extra members are invisible to contract consumers). */
export interface NoteEmission extends Emission {
  /** Index of the emission within its note: 0 hit / hold start, 1 hold end, 2 + k hold tick k (particle seed). */
  readonly seq: number;
  /** Note x (positionX / 675) and stored yOffset (yOffset * 2/900 * speed). */
  readonly x: number;
  readonly yOff: number;
  readonly above: boolean;
  readonly fxTint: RGB | null;
}

const CLICK = { kind: 'skin', name: 'click' } as const;
const DRAG = { kind: 'skin', name: 'drag' } as const;
const FLICK = { kind: 'skin', name: 'flick' } as const;

export function noteKindOf(type: number): NoteKind {
  return type === NOTE_HOLD ? 'hold' : type === NOTE_FLICK ? 'flick' : type === NOTE_DRAG ? 'drag' : 'tap';
}

/** Sound of a note's hit (or hold start). */
export function soundOf(
  type: number,
  hitsound: string | undefined,
  resolve: ((path: string) => AssetId | null) | undefined,
): Emission['sound'] {
  const fallback = type === NOTE_FLICK ? FLICK : type === NOTE_DRAG ? DRAG : CLICK;
  if (hitsound === undefined) return fallback;
  if (hitsound === 'tap.mp3') return CLICK;
  if (hitsound === 'flick.mp3') return FLICK;
  if (hitsound === 'drag.mp3') return DRAG;
  const asset = resolve?.(hitsound) ?? null;
  return asset === null ? fallback : { kind: 'asset', asset };
}

function make(
  rt: LineNotesRt,
  i: number,
  time: number,
  kind: EmissionKind,
  sound: Emission['sound'],
  fx: boolean,
  seq: number,
): NoteEmission {
  return {
    time,
    kind,
    note: rt.noteId(i),
    line: rt.line,
    sound,
    fx,
    noteKind: noteKindOf(rt.type[i] ?? 1),
    seq,
    x: rt.x[i] ?? 0,
    yOff: rt.yOff[i] ?? 0,
    above: rt.above[i] === 1,
    fxTint: rt.fxTint[i] ?? null,
  };
}

interface EmissionCache {
  revision: number;
  lineOrder: readonly LineId[] | null;
  /** Rate-independent emissions (hits, hold starts and ends), sorted. */
  base: NoteEmission[];
  baseTimes: Float64Array;
  /** Tick lists per rate (at most two rates kept). */
  ticks: Map<number, { list: NoteEmission[]; times: Float64Array }>;
  /** Real holds (for tick generation). */
  holds: Array<{ rt: LineNotesRt; i: number; order: number }>;
}

function emptyCache(): EmissionCache {
  return { revision: -1, lineOrder: null, base: [], baseTimes: new Float64Array(0), ticks: new Map(), holds: [] };
}

/** Sort order: time, then line export position, then file position of the note, then emission index. */
interface Sortable {
  readonly e: NoteEmission;
  readonly order: number;
}

function sortEmissions(items: Sortable[]): NoteEmission[] {
  items.sort((a, b) => {
    const ta = a.e.time;
    const tb = b.e.time;
    if (ta < tb) return -1;
    if (ta > tb) return 1;
    return a.order - b.order || a.e.seq - b.e.seq;
  });
  return items.map((s) => s.e);
}

function rebuildBase(ctx: EngineCompileContext, cache: EmissionCache): void {
  const resolve = (path: string): AssetId | null => ctx.assetIdForPath(path);
  const items: Sortable[] = [];
  const holds: EmissionCache['holds'] = [];
  let lineIdx = 0;
  for (const rt of runtimesInOrder(ctx)) {
    for (let i = 0; i < rt.n; i++) {
      if (rt.fake[i] === 1) continue;
      const order = lineIdx * 0x100000 + (rt.fileIndex[i] ?? 0);
      const type = rt.type[i] ?? 1;
      const sound = soundOf(type, rt.hitsound[i], resolve);
      const time = rt.time[i] ?? 0;
      if (type === NOTE_HOLD) {
        items.push({ e: make(rt, i, time, 'holdStart', sound, false, 0), order });
        items.push({ e: make(rt, i, rt.endTime[i] ?? time, 'holdEnd', null, true, 1), order });
        holds.push({ rt, i, order });
      } else {
        items.push({ e: make(rt, i, time, 'hit', sound, true, 0), order });
      }
    }
    lineIdx++;
  }
  cache.base = sortEmissions(items);
  cache.baseTimes = Float64Array.from(cache.base, (e) => e.time);
  cache.holds = holds;
  cache.ticks.clear();
}

/** The emission cache, rebuilt when any runtime or the line order changed. */
export function emissionCache(ctx: EngineCompileContext, force = false): EmissionCache {
  const g = syncAll(ctx);
  const cache = cacheOf(g, 'emissions', emptyCache);
  const order = ctx.lineOrder();
  if (force || cache.revision !== g.revision || cache.lineOrder !== order) {
    rebuildBase(ctx, cache);
    cache.revision = g.revision;
    cache.lineOrder = order;
  }
  return cache;
}

/** Hold ticks for a playback rate (sorted). */
export function tickList(cache: EmissionCache, rate: number): { list: NoteEmission[]; times: Float64Array } {
  const hit = cache.ticks.get(rate);
  if (hit !== undefined) return hit;
  const items: Sortable[] = [];
  if (rate > 0 && Number.isFinite(rate)) {
    const interval = HOLD_TICK_INTERVAL / rate;
    for (const { rt, i, order } of cache.holds) {
      const start = rt.time[i] ?? 0;
      const end = rt.endTime[i] ?? start;
      for (let k = 0; ; k++) {
        const tt = start + FRAME_DT + k * interval;
        if (!(tt < end)) break;
        items.push({ e: make(rt, i, tt, 'holdTick', null, true, 2 + k), order });
      }
    }
  }
  const list = sortEmissions(items);
  const entry = { list, times: Float64Array.from(list, (e) => e.time) };
  if (cache.ticks.size >= 2) {
    const oldest = cache.ticks.keys().next();
    if (oldest.done !== true) cache.ticks.delete(oldest.value);
  }
  cache.ticks.set(rate, entry);
  return entry;
}

/** First index with times[k] >= x (or > x when `strict`). */
function bound(times: Float64Array, x: number, strict: boolean): number {
  return strict ? Search.upperBound(times, x) : Search.lowerBound(times, x);
}

/**
 * Visits every emission with time in [t0, t1) (or (t0, t1] when `endInclusive`), merged in time order: base
 * emissions and the rate's ticks.
 */
export function forEachEmission(
  ctx: EngineCompileContext,
  t0: number,
  t1: number,
  rate: number,
  endInclusive: boolean,
  visit: (e: NoteEmission) => void,
): void {
  const cache = emissionCache(ctx);
  const ticks = tickList(cache, rate);
  let a = bound(cache.baseTimes, t0, endInclusive);
  const aEnd = bound(cache.baseTimes, t1, endInclusive);
  let b = bound(ticks.times, t0, endInclusive);
  const bEnd = bound(ticks.times, t1, endInclusive);
  while (a < aEnd || b < bEnd) {
    const ea = a < aEnd ? cache.base[a] : undefined;
    const eb = b < bEnd ? ticks.list[b] : undefined;
    if (ea !== undefined && (eb === undefined || ea.time <= eb.time)) {
      visit(ea);
      a++;
    } else if (eb !== undefined) {
      visit(eb);
      b++;
    }
  }
}

/** Whether the preview filters let an emission of `line` through (solo, hidden; focus only for sounds). */
export function emissionPasses(filters: PreviewFilters | undefined | null, line: LineId, forSound: boolean): boolean {
  if (filters === undefined || filters === null) return true;
  if (lineFilteredOut(filters, line)) return false;
  if (forSound && filters.focusHitsoundLines.size > 0 && !filters.focusHitsoundLines.has(line)) return false;
  return true;
}

/** EngineNotesModule.emissions: appends emissions with time in [t0, t1) that pass the filters. */
export function collectEmissions(
  ctx: EngineCompileContext,
  t0: number,
  t1: number,
  rate: number,
  filters: PreviewFilters | undefined,
  out: Emission[],
): void {
  if (!(t1 > t0)) return;
  forEachEmission(ctx, t0, t1, rate, false, (e) => {
    if (emissionPasses(filters, e.line, true)) out.push(e);
  });
}
